/**
 * Sync Status Indicator
 *
 * The app queues writes offline (syncQueueManager + the dexie syncQueue table)
 * and replays them on reconnect, but nothing ever surfaced that state, so a
 * user had no way to tell whether an edit had reached the server. This renders
 * the connection state and how many writes are still waiting.
 */

import { CloudOff, RefreshCw, Check } from "lucide-react";
import React, { useEffect, useState } from "react";
import { db } from "../../db";
import { useOfflineDetection } from "../../hooks/useOfflineDetection";

const POLL_INTERVAL_MS = 5000;

export const SyncStatusIndicator: React.FC = () => {
  const { isOnline, wasOffline } = useOfflineDetection();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const readQueueDepth = async () => {
      try {
        const count = await db.syncQueue.count();
        if (!cancelled) setPending(count);
      } catch {
        // An unreadable IndexedDB must not take the shell down with it.
        if (!cancelled) setPending(0);
      }
    };

    readQueueDepth();
    const timer = setInterval(readQueueDepth, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isOnline]);

  const state = !isOnline ? "offline" : pending > 0 ? "syncing" : "synced";

  // Steady state online with an empty queue needs no chrome; showing a
  // permanent green tick just trains people to ignore the indicator.
  if (state === "synced" && !wasOffline) {
    return (
      <span
        data-testid="sync-status"
        data-sync-state="synced"
        className="sr-only"
      >
        Synchronized
      </span>
    );
  }

  const label =
    state === "offline"
      ? pending > 0
        ? `Offline - ${pending} change${pending === 1 ? "" : "s"} queued`
        : "Offline - changes will be queued"
      : state === "syncing"
        ? `Synchronizing ${pending} change${pending === 1 ? "" : "s"}`
        : "Synchronized";

  const tone =
    state === "offline"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : state === "syncing"
        ? "bg-blue-50 text-blue-800 border-blue-200"
        : "bg-green-50 text-green-800 border-green-200";

  return (
    <div
      data-testid="sync-status"
      data-sync-state={state}
      role="status"
      aria-live="polite"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${tone}`}
    >
      {state === "offline" ? (
        <CloudOff data-testid="offline-indicator" className="w-3.5 h-3.5" />
      ) : state === "syncing" ? (
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Check className="w-3.5 h-3.5" />
      )}
      <span>{label}</span>
    </div>
  );
};

export default SyncStatusIndicator;
